import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import { message } from 'antd';

import { ListUserAudioLibrarysQuery } from '../../graphql/api';
import { UserAudioLibrary, IState } from '../../graphql/types';
import useUser from '../useUser';
import userAudioLibrarySubscription from '../useSubscription/useAudioLibrarySubscription';
import { setAudioLibrary, setGiideAudioLibraryFetching } from '../../actions';
import { sortCreatedAt } from '../../components/utils';
import { GiideAudioLibraryFetching } from '../../types';
import isEqual from 'lodash.isequal';

const listAudioLibrarys = gql`
  query ListUserAudioLibrarys {
    listUserAudioLibrarys(limit: 500) {
      items {
        id
        audioUrl
        createdAt
        durationSeconds
        tenantID
        title
        updatedAt
        userID
        state
        uploadSource
      }
    }
  }
`;

const useGiideAudioLibrary = () => {
  const dispatch = useDispatch();
  const userId = useUser().user?.userId;

  const [error, setError] = useState<boolean>(false);

  const data = useSelector<IState, UserAudioLibrary[] | undefined>(
    state => state.audioLibrary
  );
  const giideAudioLibraryFetching = useSelector<
    IState,
    GiideAudioLibraryFetching
  >(
    state =>
      state.giideAudioLibraryFetching || { fetching: false, refreshing: false }
  );
  const audiopLibraryItemsCount = useMemo<number>(() => {
    return data?.length || 0;
  }, [data]);

  const { subscribeUpdate, subscribeCreate, subscribeDelete } =
    userAudioLibrarySubscription(userId);

  const refetch = useCallback(
    async (refresh: boolean = false) => {
      if (!giideAudioLibraryFetching.fetching && userId) {
        dispatch(
          setGiideAudioLibraryFetching({ fetching: true, refreshing: refresh })
        );
        try {
          const result = await (API.graphql(
            graphqlOperation(listAudioLibrarys)
          ) as Promise<{
            data: ListUserAudioLibrarysQuery;
          }>);
          if (result.data.listUserAudioLibrarys?.items) {
            const sortedData =
              result.data.listUserAudioLibrarys.items.sort(sortCreatedAt);
            if (refresh) {
              if (!isEqual(sortedData, data)) {
                dispatch(setAudioLibrary(sortedData));
              }
            } else {
              dispatch(setAudioLibrary(sortedData));
            }
          }
        } catch (err) {
          message.error('Failed to fetch records from audio library.');
          setError(true);
        } finally {
          dispatch(
            setGiideAudioLibraryFetching({ fetching: false, refreshing: false })
          );
        }
      }
      if (!refresh) {
        subscribeUpdate();
        subscribeCreate();
        subscribeDelete();
      }
    },
    [
      data,
      giideAudioLibraryFetching,
      userId,
      dispatch,
      subscribeUpdate,
      subscribeCreate,
      subscribeDelete,
    ]
  );

  useEffect(() => {
    if (!data && !giideAudioLibraryFetching.fetching && !error) {
      refetch();
    }
  }, [refetch, data, giideAudioLibraryFetching, dispatch, error]);

  return { data, refetch, giideAudioLibraryFetching, audiopLibraryItemsCount };
};

export default useGiideAudioLibrary;
